import {
  AGENT_HELP_REQUESTED_ACTION_KEY,
  HUMAN_INPUT_PROVIDED_ACTION_KEY,
  PROCEDURE_LOCK_RELEASED_ACTION_KEY,
  ProcedureLockReleasedPayloadSchema,
  TASK_RETURNED_TO_AGENT_ACTION_KEY,
} from '@erp/shared-types';

export type HumanActionPayload = Record<string, unknown> | null;

/** How the timeline should render a recorded human action's payload. */
export type HumanActionDetailView =
  | { kind: 'none' }
  | {
      kind: 'help_request';
      question: string | null;
      context: string | null;
      attempted: string | null;
    }
  | { kind: 'reply'; response: string }
  | {
      kind: 'returned_to_agent';
      note: string;
      targetAgentDefinitionId: string | null;
      /** A person took the waiting task from the agent, not re-queued it. */
      detached: boolean;
    }
  | { kind: 'procedure_lock_released'; name: string; releasedVersion: number }
  | { kind: 'json'; payload: Record<string, unknown> };

export function stringField(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

/**
 * Agent help requests, human replies, and the chat agent's hand-back note
 * read as a conversation; anything else (or a malformed payload for those
 * keys) falls back to raw JSON.
 */
export function describeHumanAction(
  actionKey: string,
  payload: HumanActionPayload,
): HumanActionDetailView {
  if (!payload || Object.keys(payload).length === 0) return { kind: 'none' };

  if (actionKey === AGENT_HELP_REQUESTED_ACTION_KEY) {
    const question = stringField(payload.question);
    const context = stringField(payload.context);
    const attempted = stringField(payload.attempted);
    if (!question && !context && !attempted) return { kind: 'json', payload };
    return { kind: 'help_request', question, context, attempted };
  }

  if (actionKey === HUMAN_INPUT_PROVIDED_ACTION_KEY) {
    const response = stringField(payload.response);
    return response ? { kind: 'reply', response } : { kind: 'json', payload };
  }

  if (actionKey === PROCEDURE_LOCK_RELEASED_ACTION_KEY) {
    const parsed = ProcedureLockReleasedPayloadSchema.safeParse(payload);
    return parsed.success
      ? {
          kind: 'procedure_lock_released',
          name: parsed.data.name,
          releasedVersion: parsed.data.releasedVersion,
        }
      : { kind: 'json', payload };
  }

  if (actionKey === TASK_RETURNED_TO_AGENT_ACTION_KEY) {
    const note = stringField(payload.note);
    return note
      ? {
          kind: 'returned_to_agent',
          note,
          targetAgentDefinitionId: stringField(payload.targetAgentDefinitionId),
          detached: payload.detached === true,
        }
      : { kind: 'json', payload };
  }

  return { kind: 'json', payload };
}
